import React, { useRef, useEffect, Fragment } from 'react';
import { PropTypes } from 'prop-types';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Label,
  Tooltip
} from 'recharts';

import { TYPES } from 'envs';
import CustomLabel from './customLabel';
import CustomTooltip from './customTooltip';
import { newLine, newText } from './utils';
import { customAcronym, customColor, getSite } from '../../constants';

const alias = new Date().getTime();

const AtresPieChart = ({
  data: data1,
  data2,
  showLabel,
  showMajorityLine,
  innerData,
  seatTotal,
  dataKey = 'votesPercent',
  isSenate,
  unitLabel,
  unitAbrv,
  chartInfo1 = {
    innerRadius: 125,
    outerRadius: 155,
    label: 2021
  },
  chartInfo2 = {
    innerRadius: 90,
    outerRadius: 120,
    label: 2020
  },
  type
}) => {
  const totalInData = data1.reduce((tot, current) => {
    tot += current[dataKey];
    return tot;
  }, 0);
  const data =
    totalInData === seatTotal
      ? data1
      : data1.concat([
          {
            id: -1,
            name: 'Sin asignar',
            partyAcronym: 'Sin asignar',
            partyColor: '#FFFFFF',
            seats: seatTotal - totalInData,
            votes: 0,
            votesPercent: 0
          }
        ]);
  const svg = useRef(null);
  const getLabel = (items, unit) => {
    const labelMunicipal = items === 1 ? 'concejal' : 'concejales';
    const labelAutonomic = items === 1 ? 'escaño' : 'escaños';
    const labelGeneral = items === 1 ? 'escaño' : 'escaños';
    const LABEL = {
      [TYPES.MUNICIPAL]: labelMunicipal,
      [TYPES.REGIONAL]: labelAutonomic,
      [TYPES.GENERAL]: labelGeneral
    };
    const label = LABEL[type];

    if (unit === unitLabel || isSenate) {
      if (items > 1) {
        return 'Senadores';
      }
      return 'Senador';
    }
    if (unit === unitAbrv || isSenate) {
      if (items > 1) {
        return 'Senadores';
      }
      return 'Senador';
    }
    if (items > 1) {
      return `${label}`;
    }
    return label;
  };
  const uLabel = items => getLabel(items, 'unitLabel');
  const uAbrv = items => getLabel(items, 'unitAbrv');

  useEffect(() => {
    if (seatTotal) {
      setTimeout(() => {
        try {
          const selSvg = svg.current.container.getElementsByTagName('svg');
          const textSvg = svg.current.container.querySelector(
            `._middleLine${alias}`
          );
          const line2 = svg.current.container.querySelector(
            `._mayoria${alias}`
          );
          if (textSvg && line2) {
            textSvg.remove();
            line2.remove();
          }
          if (
            selSvg &&
            selSvg[0] &&
            showMajorityLine &&
            (!textSvg || !textSvg.length) &&
            (!line2 || !line2.length)
          ) {
            selSvg[0].append(showLabel ? newLine(alias) : '');
            selSvg[0].append(
              showLabel ? newText(Math.trunc(seatTotal / 2 + 1), alias) : ''
            );
          }
        } catch (error) {}
      }, 500);
    }
  }, [seatTotal, showMajorityLine]);

  if (data2) {
    return (
      <ResponsiveContainer
        className="doubleChart"
        minHeight="226px"
        height="100%"
        ref={svg}
      >
        <PieChart>
          <Tooltip
            content={<CustomTooltip unitName={uAbrv} isSenate={isSenate} />}
          />
          <Pie
            dataKey={dataKey}
            data={data.filter(current => current[dataKey] >= 1)}
            startAngle={180}
            endAngle={0}
            label={CustomLabel}
            innerRadius={chartInfo1.innerRadius}
            outerRadius={chartInfo1.outerRadius}
            isAnimationActive={false}
            offset={0}
            cy="90%"
          >
            <line x1="0" y1="0" x2="200" y2="200" />
            {data
              .filter(current => current[dataKey] >= 1)
              .map((entry, index) => (
                <Cell
                  key={`${customAcronym(entry, getSite())}a`}
                  fill={customColor(entry, getSite())}
                  stroke={entry.id === -1 ? '#454545' : ''}
                  strokeWidth={entry.id === -1 ? 1 : ''}
                />
              ))}
            {showLabel && (
              <Label
                value={`${seatTotal} ${uLabel(seatTotal)}`}
                position="center"
              />
            )}
          </Pie>
          {data2 && (
            <Pie
              dataKey={dataKey}
              data={data2.filter(current => current[dataKey] >= 1)}
              startAngle={180}
              endAngle={0}
              label={CustomLabel}
              innerRadius={chartInfo2.innerRadius}
              outerRadius={chartInfo2.outerRadius}
              isAnimationActive={false}
              offset={0}
              cy="90%"
            >
              <line x1="0" y1="0" x2="200" y2="200" />
              {data2
                .filter(current => current[dataKey] >= 1)
                .map((entry, index) => (
                  <Cell
                    key={customAcronym(entry, getSite())}
                    fill={customColor(entry, getSite())}
                    stroke={entry.id === -1 ? '#454545' : ''}
                    strokeWidth={entry.id === -1 ? 1 : ''}
                  />
                ))}
              {showLabel && (
                <Label
                  value={`${seatTotal} ${uLabel(seatTotal)}`}
                  position="center"
                />
              )}
            </Pie>
          )}

          <text
            id="svgText"
            className="year year_current"
            x="10%"
            y="93%"
            textAnchor="left"
          >
            {chartInfo1.label}
          </text>
          <text
            id="svgText"
            className="year year_last"
            x="20%"
            y="93%"
            textAnchor="left"
          >
            {chartInfo2.label}
          </text>
        </PieChart>
      </ResponsiveContainer>
    );
  }
  return (
    <ResponsiveContainer ref={svg} minHeight="257px" height="100%">
      <PieChart>
        <Tooltip content={<CustomTooltip unitName={uAbrv} />} />
        <Pie
          dataKey={dataKey}
          data={data}
          startAngle={180}
          endAngle={0}
          label={CustomLabel}
          innerRadius="80%"
          outerRadius="135%"
          isAnimationActive={false}
          offset={0}
          cy="90%"
        >
          <line x1="0" y1="0" x2="200" y2="200" />
          {data.map((entry, index) => (
            <Cell
              key={customAcronym(entry, getSite())}
              fill={customColor(entry, getSite())}
            />
          ))}
          {showLabel && (
            <Label
              value={`${seatTotal} ${uLabel(seatTotal)}`}
              position="center"
            />
          )}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  );
};

AtresPieChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.number
    })
  ),
  seatTotal: PropTypes.number,
  innerData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.number
    })
  ),
  innerDataSeatTotal: PropTypes.number,
  showLabel: PropTypes.bool
};

AtresPieChart.defaultProps = {
  showTable: false,
  innerDate: undefined,
  showLabel: true
};

export default AtresPieChart;
